// frontend/src/components/common/MultiLanguageField.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import axios from '../../axiosConfig';
import referenceService from '../../services/referenceService';
import TranslationModal from './TranslationModal';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * MultiLanguageField Component
 *
 * Provides a tabbed interface for managing content in multiple languages.
 * - Main content shown in entity's default language (not necessarily English)
 * - Translation tabs for other languages
 * - Clear visual indicators for default vs translated content
 * - Admin-only interface for managing translations
 *
 * @param {Object} props
 * @param {number} props.entityId - Entity ID (null for global scope)
 * @param {string} props.entityDefaultLanguage - Default language for the entity
 * @param {boolean} props.multiLanguageEnabled - Whether multi-language is enabled for the entity
 * @param {string} props.contentType - Type of content (route, entity, etc.)
 * @param {number} props.contentId - ID of the content item
 * @param {string} props.fieldName - Field being translated (name, description)
 * @param {string} props.defaultValue - Current value in default language
 * @param {Function} props.onChange - Callback when translations change
 * @param {Function} props.onBlur - Callback when input loses focus
 * @param {boolean} props.required - Whether the default language field is required
 * @param {string} props.label - Display label for the field
 * @param {boolean} props.multiline - Whether to use textarea (default: false)
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {boolean} props.isGlobal - Whether this is global-scoped content (default: false)
 * @param {number} props.eventTypeId - For global notification templates
 * @param {string} props.channel - For global notification templates
 */
const MultiLanguageField = ({
    entityId,
    entityDefaultLanguage = 'en',
    multiLanguageEnabled = false,
    contentType,
    contentId,
    fieldName,
    defaultValue = '',
    onChange,
    onBlur,
    required = false,
    label,
    multiline = false,
    disabled = false,
    isGlobal = false,
    eventTypeId = null,
    channel = null
}) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [translationStats, setTranslationStats] = useState({ translated: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    
    // Load translation statistics when multi-language is enabled and contentId exists
    useEffect(() => {
        if (multiLanguageEnabled && contentId) {
            loadTranslationStats();
        }
    }, [multiLanguageEnabled, contentId, entityId, contentType, fieldName, isGlobal]);

    const loadTranslationStats = async () => {
        try {
            setLoading(true);

            // Get available languages for counting
            const languages = await referenceService.getLanguages();
            const scopeDefaultLanguage = isGlobal ? 'en' : entityDefaultLanguage;
            const nonDefaultLanguages = languages.filter(l => l.language_code !== scopeDefaultLanguage);

            // Get existing translations if contentId exists
            if (contentId) {
                // Use entityId from context (even for global, we need to pass the current entityId in URL)
                const apiEntityId = entityId || 1; // Fallback to 1 if null
                const response = await axios.get(`/api/translations/entity/${apiEntityId}/translations`, {
                    params: {
                        contentType,
                        contentId,
                        isGlobal: isGlobal ? 'true' : 'false'
                    }
                });
                const data = response.data.data;
                const fieldTranslations = data.translations[fieldName] || {};

                // Count translated languages
                const translatedCount = Object.keys(fieldTranslations).filter(
                    lang => lang !== scopeDefaultLanguage && fieldTranslations[lang]
                ).length;

                setTranslationStats({
                    translated: translatedCount,
                    total: nonDefaultLanguages.length
                });
            } else {
                setTranslationStats({
                    translated: 0,
                    total: nonDefaultLanguages.length
                });
            }

        } catch (error) {
            console.error('Error loading translation stats:', error);
            setTranslationStats({ translated: 0, total: 0 });
        } finally {
            setLoading(false);
        }
    };
    
    const handleDefaultValueChange = (value) => {
        // For default language, we update the main content, not translations
        if (onChange) {
            onChange(fieldName, value, true); // true indicates this is default content
        }
    };
    
    const handleModalSave = (translations) => {
        // Refresh stats after saving
        loadTranslationStats();
        
        // Call parent onChange if needed
        if (onChange) {
            onChange(fieldName, translations, false);
        }
    };
    
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };
    
    // If multi-language is not enabled, render a simple input field
    if (!multiLanguageEnabled) {
        if (multiline) {
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {label}
                        {required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <textarea
                        value={defaultValue}
                        onChange={(e) => onChange(fieldName, e.target.value, true)}
                        onBlur={onBlur}
                        disabled={disabled}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                        placeholder={t('enter_content')}
                    />
                </div>
            );
        } else {
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {label}
                        {required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                        type="text"
                        value={defaultValue}
                        onChange={(e) => onChange(fieldName, e.target.value, true)}
                        onBlur={onBlur}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                        placeholder={t('enter_content')}
                    />
                </div>
            );
        }
    }
    
    // Multi-language enabled: show field with translation button
    // Hide button if disabled and no translations exist (nothing to view)
    const shouldShowButton = !disabled || translationStats.translated > 0;
    // Disable button if no contentId (item not saved yet)
    const isButtonDisabled = loading || !contentId;

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className={cn("block text-sm font-medium", text.primary)}>
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {shouldShowButton && (
                    <button
                        type="button"
                        onClick={handleOpenModal}
                        disabled={isButtonDisabled}
                        title={!contentId ? t('save_first_to_manage_translations') : undefined}
                        className={cn(
                            "inline-flex items-center gap-2 px-3 py-1 text-sm rounded-md transition-colors",
                            isButtonDisabled
                                ? cn("cursor-not-allowed", background.disabled, text.secondary)
                                : cn("hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400", border.default, "border")
                        )}
                    >
                        <Languages className="w-4 h-4" />
                        <span>{t('manage_translations')}</span>
                        {translationStats.total > 0 && (
                            <span className={cn(
                                "px-1.5 py-0.5 text-xs rounded-full",
                                translationStats.translated > 0
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            )}>
                                {translationStats.translated}/{translationStats.total}
                            </span>
                        )}
                    </button>
                )}
            </div>
            
            {multiline ? (
                <textarea
                    value={defaultValue}
                    onChange={(e) => handleDefaultValueChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={disabled}
                    rows={4}
                    className={cn(
                        "w-full px-3 py-2 rounded-md",
                        background.input,
                        border.default,
                        text.primary,
                        "border focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                    placeholder={t('enter_content')}
                />
            ) : (
                <input
                    type="text"
                    value={defaultValue}
                    onChange={(e) => handleDefaultValueChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={disabled}
                    className={cn(
                        "w-full px-3 py-2 rounded-md",
                        background.input,
                        border.default,
                        text.primary,
                        "border focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                    placeholder={t('enter_content')}
                />
            )}
            
            {/* Translation Modal */}
            <TranslationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                entityId={entityId}
                entityDefaultLanguage={entityDefaultLanguage}
                contentType={contentType}
                contentId={contentId}
                fieldName={fieldName}
                defaultValue={defaultValue}
                onSave={handleModalSave}
                multiline={multiline}
                isGlobal={isGlobal}
                eventTypeId={eventTypeId}
                channel={channel}
                readOnly={disabled}
            />
        </div>
    );
};

export default MultiLanguageField;